import {
  getAtasPrecos,
  getAtasPrecosEligibility,
  getAtasPrecosFornecedores,
  getAtasPrecosItens,
  criarAtaPreco,
  gerarAtaPreco,
  atualizarAtaPrecoRascunho,
  publicarAtaPreco,
  criarAtaPrecoDocumento,
  deletarAtaPrecoDocumento,
  criarAtaPrecoParteEnvolvida,
  deletarAtaPrecoParteEnvolvida,
  retificarAtaPreco,
  deletarAtaPreco,
} from '@/resources/ofertasCompra';
import { TIPO_DOCUMENTO_ARP_PRINCIPAL } from '@/views/Pregao/Arp/helpers';

const ATAS_PRECOS = 'ATAS_PRECOS';
const ATAS_PRECOS_ADICIONAR = 'ATAS_PRECOS_ADICIONAR';
const ATAS_PRECOS_REMOVER = 'ATAS_PRECOS_REMOVER';
const ATAS_PRECOS_LIMPAR = 'ATAS_PRECOS_LIMPAR';
const ATAS_PRECOS_UPLOAD = 'ATAS_PRECOS_UPLOAD';
const ATAS_PRECOS_ZERAR_UPLOAD = 'ATAS_PRECOS_ZERAR_UPLOAD';
const ATAS_PRECOS_ELIGIBILITY = 'ATAS_PRECOS_ELIGIBILITY';
const ARP_JOURNEY = 'ARP_JOURNEY';
const ARP_JOURNEY_RESET = 'ARP_JOURNEY_RESET';
const ARP_DRAFT = 'ARP_DRAFT';
const ARP_ITENS_SELECAO = 'ARP_ITENS_SELECAO';
const ARP_FORNECEDORES = 'ARP_FORNECEDORES';

const getPrincipalArquivo = ata => {
  const docs = Array.isArray(ata?.documentos) ? ata.documentos : [];
  const doc = docs.find(d => {
    const tipo = Number(d.tipoDocumentoId ?? d.tipoDocumento);
    return tipo === TIPO_DOCUMENTO_ARP_PRINCIPAL;
  });
  return doc?.arquivo || null;
};

const defaultEligibility = () => ({
  canUploadArp: false,
  checks: {
    isRegistro: false,
    hasHomologatedItem: false,
    hasOperationPermission: false,
    canOperatePregoes: false,
    canOperateConcorrencias: false,
    canOperateDispensas: false,
  },
});

const defaultJourney = () => ({
  active: false,
  type: null, // 'vencedor' | 'reserva' | 'propria'
  step: 'choice', // choice | fill | itens | review | done
  mode: 'create', // create | edit
  isRascunho: true,
  agruparPorFornecedor: false,
});

const defaultDraft = () => ({
  fornecedorId: null,
  fornecedorNome: '',
  papel: 'vencedor', // vencedor | adesao | reserva
  numero: '',
  ano: '',
  vigenciaInicio: null,
  vigenciaFim: null,
  permitirCarona: false,
  dataAssinatura: null,
  arquivo: null,
  itensSelecionados: [],
  /** IDs reais de OfertasCompraItens (payload de gerar). */
  itensIds: [],
  ataId: null,
  arquivoGerado: null,
  /** Obrigatória na retificação (edit de ARP publicada / PNCP). */
  justificativa: '',
});

function normalizeListResponse(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

/** Agrupa itens do endpoint ARP por OfertasCompraFornecedores.id (vencedor informativo). */
function buildGruposFromArpItens(itens = []) {
  const byForn = new Map();
  (itens || []).forEach(item => {
    const id = Number(item.id);
    if (!Number.isFinite(id) || id <= 0) return;

    const fid = Number(item.fornecedorId) || 0;
    const key = Number.isFinite(fid) && fid > 0 ? fid : 0;
    if (!byForn.has(key)) {
      byForn.set(key, {
        fornecedorId: key || null,
        fornecedorNome: item.fornecedorNome || (key ? `Fornecedor #${key}` : 'Sem vencedor'),
        itens: [],
      });
    }
    byForn.get(key).itens.push({
      id,
      numero: Number(item.numero) || id,
      descricao: item.descricao || '',
      selecionado: false,
    });
  });
  return Array.from(byForn.values()).sort((a, b) =>
    String(a.fornecedorNome).localeCompare(String(b.fornecedorNome), 'pt-BR'),
  );
}

function itemEstaSelecionado(selected, item) {
  if (item.selecionado) return true;
  if (!selected || !selected.size) return false;
  return (
    selected.has(item.numero) ||
    selected.has(Number(item.numero)) ||
    selected.has(String(item.numero))
  );
}

/**
 * Resolve ids de OfertasCompraItens a partir dos números selecionados na UI.
 * O vencedor do item (grupo) é só informativo — não define o fornecedor da ARP.
 */
function resolveItensIdsSelecionados(grupos, numerosSelecionados) {
  const selected = new Set(numerosSelecionados || []);
  const ids = [];
  (grupos || []).forEach(grupo => {
    (grupo.itens || []).forEach(item => {
      if (!itemEstaSelecionado(selected, item)) return;
      const id = Number(item.id);
      if (Number.isFinite(id) && id > 0) ids.push(id);
    });
  });
  return ids;
}

const ARP_GESTAO_PENDENTES = 'ARP_GESTAO_PENDENTES';

const mutations = {
  [ATAS_PRECOS](state, data) {
    state.atas = Array.isArray(data) ? data : [];
  },
  [ATAS_PRECOS_ADICIONAR](state, data) {
    state.atas = [data, ...state.atas];
  },
  [ATAS_PRECOS_REMOVER](state, id) {
    const index = state.atas.findIndex(ata => ata.id === id);
    if (index > -1) {
      state.atas.splice(index, 1);
    }
  },
  [ATAS_PRECOS_LIMPAR](state) {
    state.atas = [];
  },
  [ATAS_PRECOS_UPLOAD](state, progresso) {
    const total = Number(progresso?.total || 0);
    const loaded = Number(progresso?.loaded || 0);
    state.uploadProgress = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  },
  [ATAS_PRECOS_ZERAR_UPLOAD](state) {
    state.uploadProgress = 0;
  },
  [ATAS_PRECOS_ELIGIBILITY](state, data) {
    state.eligibility = data || defaultEligibility();
  },
  [ARP_JOURNEY](state, payload) {
    state.journey = { ...state.journey, ...payload };
  },
  [ARP_JOURNEY_RESET](state) {
    state.journey = defaultJourney();
    state.draft = defaultDraft();
    state.itensSelecao = [];
  },
  [ARP_DRAFT](state, payload) {
    state.draft = { ...state.draft, ...payload };
  },
  [ARP_ITENS_SELECAO](state, data) {
    state.itensSelecao = Array.isArray(data) ? data : [];
  },
  [ARP_FORNECEDORES](state, data) {
    state.fornecedoresArp = Array.isArray(data) ? data : [];
  },
  [ARP_GESTAO_PENDENTES](state, data) {
    state.gestaoPendentes = Array.isArray(data) ? data : [];
  },
};

const actions = {
  async buscarOfertaCompraAtasPrecos({ commit }, ofertaCompraId) {
    const response = await getAtasPrecos(ofertaCompraId);
    if (response.status !== 200) return Promise.reject(response);
    return commit(ATAS_PRECOS, response.data.data);
  },
  async buscarOfertaCompraAtasPrecosEligibility({ commit }, ofertaCompraId) {
    const response = await getAtasPrecosEligibility(ofertaCompraId);
    if (response.status !== 200) return Promise.reject(response);
    return commit(ATAS_PRECOS_ELIGIBILITY, response.data.data);
  },
  async adicionarOfertaCompraAtaPreco({ commit, rootState }, dados) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await criarAtaPreco(ofertaCompraId, dados, {
      onUploadProgress: progress => commit(ATAS_PRECOS_UPLOAD, progress),
    });
    if (response.status !== 201) return Promise.reject(response);
    return commit(ATAS_PRECOS_ADICIONAR, response.data.data);
  },
  async publicarOfertaCompraAtaPreco({ commit, dispatch, rootState }, { ataId, formData }) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await publicarAtaPreco(ofertaCompraId, ataId, formData, {
      onUploadProgress: progress => commit(ATAS_PRECOS_UPLOAD, progress),
    });
    if (response.status !== 200) return Promise.reject(response);
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    return response.data?.data;
  },
  async adicionarOfertaCompraAtaPrecoDocumento(
    { commit, dispatch, rootState },
    { ataId, formData },
  ) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await criarAtaPrecoDocumento(ofertaCompraId, ataId, formData, {
      onUploadProgress: progress => commit(ATAS_PRECOS_UPLOAD, progress),
    });
    if (response.status !== 201) return Promise.reject(response);
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    return response.data?.data;
  },
  async removerOfertaCompraAtaPrecoDocumento(
    { dispatch, rootState },
    { ataId, documentoId, justificativa },
  ) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await deletarAtaPrecoDocumento(ofertaCompraId, ataId, documentoId, {
      justificativa,
    });
    if (response.status !== 204) return Promise.reject(response);
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    return response;
  },
  async adicionarOfertaCompraAtaPrecoParteEnvolvida({ dispatch, rootState }, { ataId, dados }) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await criarAtaPrecoParteEnvolvida(ofertaCompraId, ataId, dados);
    if (response.status !== 201) return Promise.reject(response);
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    return response.data?.data;
  },
  async removerOfertaCompraAtaPrecoParteEnvolvida(
    { dispatch, rootState },
    { ataId, parteId, justificativa },
  ) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await deletarAtaPrecoParteEnvolvida(ofertaCompraId, ataId, parteId, {
      justificativa,
    });
    if (response.status !== 204) return Promise.reject(response);
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    return response;
  },
  async retificarOfertaCompraAtaPreco({ dispatch, rootState }, { ataId, dados }) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const response = await retificarAtaPreco(ofertaCompraId, ataId, dados);
    if (response.status !== 200) return Promise.reject(response);
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    return response.data?.data;
  },
  async removerOfertaCompraAtaPreco({ commit, rootState }, payload) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const id =
      typeof payload === 'object' && payload !== null && payload.id != null ? payload.id : payload;
    const body =
      typeof payload === 'object' &&
      payload !== null &&
      Object.prototype.hasOwnProperty.call(payload, 'justificativa')
        ? { justificativa: payload.justificativa }
        : undefined;
    const response = await deletarAtaPreco(ofertaCompraId, id, body);
    if (response.status !== 204) return Promise.reject(response);
    commit(ATAS_PRECOS_REMOVER, id);
    return response;
  },
  limparAtasPrecosPregao({ commit }) {
    commit(ATAS_PRECOS_LIMPAR);
  },
  zerarAtasPrecosUpload({ commit }) {
    commit(ATAS_PRECOS_ZERAR_UPLOAD);
  },
  iniciarArpJourney({ commit }, payload = null) {
    commit(ARP_JOURNEY_RESET);
    const options =
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? payload
        : { type: payload };
    const type = options.type || null;
    if (options.draft) {
      commit(ARP_DRAFT, options.draft);
    }
    commit(ARP_JOURNEY, {
      active: true,
      type,
      step: type ? 'fill' : 'choice',
      mode: options.mode === 'edit' ? 'edit' : 'create',
      isRascunho: options.isRascunho !== false,
    });
  },
  atualizarArpJourney({ commit }, payload) {
    commit(ARP_JOURNEY, payload);
  },
  atualizarArpDraft({ commit }, payload) {
    commit(ARP_DRAFT, payload);
  },
  cancelarArpJourney({ commit }) {
    commit(ARP_JOURNEY_RESET);
  },
  async carregarArpFornecedores({ commit, rootState }) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra?.id;
    if (!ofertaCompraId) {
      commit(ARP_FORNECEDORES, []);
      return [];
    }
    const response = await getAtasPrecosFornecedores(ofertaCompraId);
    if (response.status !== 200) return Promise.reject(response);
    const raw = response.data;
    let data = [];
    if (Array.isArray(raw)) {
      data = raw;
    } else if (Array.isArray(raw?.data)) {
      data = raw.data;
    }
    commit(ARP_FORNECEDORES, data);
    return data;
  },
  async carregarArpGestaoPendentes({ commit }) {
    // Sem endpoint dedicado ainda: lista vazia (sem mock).
    commit(ARP_GESTAO_PENDENTES, []);
    return [];
  },
  async carregarArpItensSelecao({ commit, state, rootState }) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra?.id;
    // Limpa imediatamente qualquer seleção anterior (ex.: estado stale de HMR).
    commit(ARP_ITENS_SELECAO, []);
    commit(ARP_DRAFT, { itensSelecionados: [], itensIds: [] });

    if (!ofertaCompraId) {
      return [];
    }

    const params = {};
    // Cadastro reserva: só homologados de vencedores ≠ titular dos metadados.
    const titularId = Number(state.draft?.fornecedorId);
    if (state.journey?.type === 'reserva' && Number.isFinite(titularId) && titularId > 0) {
      params.excetoFornecedorId = titularId;
    }

    const response = await getAtasPrecosItens(ofertaCompraId, params);
    if (response.status !== 200) return Promise.reject(response);
    const data = buildGruposFromArpItens(normalizeListResponse(response?.data));
    commit(ARP_ITENS_SELECAO, data);
    return data;
  },
  atualizarArpItensSelecao({ commit }, data) {
    commit(ARP_ITENS_SELECAO, data);
  },
  /**
   * Conclui a jornada: create gera/envia; edit rascunho regenera/atualiza;
   * edit publicada retifica no PNCP.
   */
  async concluirArpJourney({ commit, dispatch, state, rootState }) {
    const { type, mode, isRascunho } = state.journey;
    const { draft } = state;
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const isEdit = mode === 'edit';

    if (isEdit && !isRascunho) {
      const justificativa = String(draft.justificativa || '').trim();
      if (justificativa.length < 10 || justificativa.length > 255) {
        return Promise.reject(
          new Error('Informe a justificativa da retificação (10 a 255 caracteres).'),
        );
      }
      if (!draft.dataAssinatura) {
        return Promise.reject(new Error('Data de assinatura é obrigatória na retificação'));
      }
      if (!draft.ataId) {
        return Promise.reject(new Error('Ata não encontrada para retificação'));
      }

      const response = await retificarAtaPreco(ofertaCompraId, draft.ataId, {
        numeroAtaRegistroPreco: draft.numero,
        numAta: draft.numero,
        dataAssinatura: draft.dataAssinatura,
        dataVigenciaInicio: draft.vigenciaInicio,
        dataVigenciaFim: draft.vigenciaFim,
        possibilidadeAdesao: Boolean(draft.permitirCarona),
        justificativa,
      });
      if (response.status !== 200) return Promise.reject(response);
      await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
      commit(ARP_JOURNEY, { step: 'done' });
      return { type, mode: 'edit', ata: response.data?.data };
    }

    if (isEdit && isRascunho) {
      if (!draft.ataId) {
        return Promise.reject(new Error('Ata não encontrada para atualização'));
      }

      if (type === 'propria') {
        const payload = {
          origem: 'PROPRIA',
          numeroAtaRegistroPreco: draft.numero,
          anoAta: Number(draft.ano),
          dataAssinatura: draft.dataAssinatura || null,
          dataVigenciaInicio: draft.vigenciaInicio,
          dataVigenciaFim: draft.vigenciaFim,
          possibilidadeAdesao: Boolean(draft.permitirCarona),
        };
        const response = await atualizarAtaPrecoRascunho(ofertaCompraId, draft.ataId, payload);
        if (response.status !== 200) return Promise.reject(response);
        const ata = response.data?.data || null;
        commit(ARP_DRAFT, {
          ataId: ata?.id || draft.ataId,
          arquivoGerado: getPrincipalArquivo(ata) || draft.arquivoGerado,
        });
        await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
        commit(ARP_JOURNEY, { step: 'done' });
        return { type, mode: 'edit', ata };
      }

      const fornecedorId = Number(draft.fornecedorId);
      if (!Number.isFinite(fornecedorId) || fornecedorId <= 0) {
        return Promise.reject(new Error('Fornecedor é obrigatório'));
      }

      const payload = {
        origem: type === 'reserva' ? 'RESERVA' : 'VENCEDOR',
        fornecedorId,
        numeroAtaRegistroPreco: draft.numero,
        anoAta: Number(draft.ano),
        dataVigenciaInicio: draft.vigenciaInicio,
        dataVigenciaFim: draft.vigenciaFim,
        possibilidadeAdesao: Boolean(draft.permitirCarona),
      };

      const precisaSelecaoItens = type === 'reserva';
      if (precisaSelecaoItens) {
        const itensIdsFromDraft = (draft.itensIds || [])
          .map(Number)
          .filter(n => Number.isFinite(n) && n > 0);
        const itensIds =
          itensIdsFromDraft.length > 0
            ? itensIdsFromDraft
            : resolveItensIdsSelecionados(state.itensSelecao, draft.itensSelecionados);
        if (!itensIds.length) {
          return Promise.reject(new Error('Selecione ao menos um item'));
        }
        payload.escopoItens = 'selecao';
        payload.itensIds = itensIds;
      }

      const response = await atualizarAtaPrecoRascunho(ofertaCompraId, draft.ataId, payload);
      if (response.status !== 200) return Promise.reject(response);
      const ata = response.data?.data || null;
      commit(ARP_DRAFT, {
        ataId: ata?.id || draft.ataId,
        arquivoGerado: getPrincipalArquivo(ata),
      });
      await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
      commit(ARP_JOURNEY, { step: 'done' });
      return { type, mode: 'edit', ata };
    }

    if (type === 'propria') {
      const { arquivo } = draft;
      const file = Array.isArray(arquivo) ? arquivo[0] : arquivo;
      if (!file) {
        return Promise.reject(new Error('Arquivo da ARP é obrigatório'));
      }
      const formData = new FormData();
      formData.append('numeroAtaRegistroPreco', draft.numero);
      formData.append('anoAta', draft.ano);
      formData.append('dataAssinatura', draft.dataAssinatura);
      formData.append('dataVigenciaInicio', draft.vigenciaInicio);
      formData.append('dataVigenciaFim', draft.vigenciaFim);
      formData.append('possibilidadeAdesao', draft.permitirCarona ? 'true' : 'false');
      formData.append('file', file);

      await dispatch('adicionarOfertaCompraAtaPreco', formData);
      await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
      commit(ARP_JOURNEY, { step: 'done' });
      return { type: 'propria' };
    }

    const fornecedorId = Number(draft.fornecedorId);
    if (!Number.isFinite(fornecedorId) || fornecedorId <= 0) {
      return Promise.reject(new Error('Fornecedor é obrigatório'));
    }

    const payload = {
      origem: type === 'reserva' ? 'RESERVA' : 'VENCEDOR',
      fornecedorId,
      numeroAtaRegistroPreco: draft.numero,
      anoAta: Number(draft.ano),
      dataVigenciaInicio: draft.vigenciaInicio,
      dataVigenciaFim: draft.vigenciaFim,
      possibilidadeAdesao: Boolean(draft.permitirCarona),
    };

    // Vencedor: escopo padrão na API (só homologados daquele fornecedor).
    // Reserva / adesão: itens escolhidos na UI (vencedor do item é informativo).
    const precisaSelecaoItens = type === 'reserva';
    if (precisaSelecaoItens) {
      const itensIdsFromDraft = (draft.itensIds || [])
        .map(Number)
        .filter(n => Number.isFinite(n) && n > 0);
      const itensIds =
        itensIdsFromDraft.length > 0
          ? itensIdsFromDraft
          : resolveItensIdsSelecionados(state.itensSelecao, draft.itensSelecionados);
      if (!itensIds.length) {
        return Promise.reject(new Error('Selecione ao menos um item'));
      }
      payload.escopoItens = 'selecao';
      payload.itensIds = itensIds;
    }

    const response = await gerarAtaPreco(ofertaCompraId, payload);
    if (response.status !== 201) return Promise.reject(response);
    const ata = response.data?.data || null;
    const arquivoGerado = getPrincipalArquivo(ata);

    commit(ARP_DRAFT, {
      ataId: ata?.id || null,
      arquivoGerado,
    });
    await dispatch('buscarOfertaCompraAtasPrecos', ofertaCompraId);
    commit(ARP_JOURNEY, { step: 'done' });
    return { type, ata };
  },
};
const getters = {
  obterOfertaCompraAtasPrecos(state) {
    return state.atas;
  },
  obterOfertaCompraAtasPrecosEligibility(state) {
    return state.eligibility;
  },
  /** Pessoa + situação: controla exibição da aba ARP na sessão. */
  canAccessArpTab(state) {
    return state.eligibility && state.eligibility.canUploadArp === true;
  },
  obterAtasPrecosUploadProgress(state) {
    return state.uploadProgress;
  },
  obterArpJourney(state) {
    return state.journey;
  },
  obterArpDraft(state) {
    return state.draft;
  },
  obterArpItensSelecao(state) {
    return state.itensSelecao;
  },
  obterArpFornecedores(state) {
    return state.fornecedoresArp;
  },
  obterArpGestaoPendentes(state) {
    return state.gestaoPendentes;
  },
  obterArpItensFlat(state) {
    const byNumero = new Map();
    state.itensSelecao.forEach(grupo => {
      (grupo.itens || []).forEach(item => {
        if (!byNumero.has(item.numero)) {
          byNumero.set(item.numero, {
            ...item,
            fornecedorId: grupo.fornecedorId,
            vencedor: grupo.fornecedorNome || '—',
          });
        }
      });
    });
    return Array.from(byNumero.values()).sort((a, b) => a.numero - b.numero);
  },
};

const state = {
  atas: [],
  uploadProgress: 0,
  eligibility: defaultEligibility(),
  journey: defaultJourney(),
  draft: defaultDraft(),
  itensSelecao: [],
  fornecedoresArp: [],
  gestaoPendentes: [],
};

export default {
  state,
  mutations,
  actions,
  getters,
};
